import { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Paper, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody,
  Grid, TextField, Button, MenuItem, Dialog, Chip, Divider,
  Collapse, IconButton
} from "@mui/material";
import ExpandMoreIcon    from "@mui/icons-material/ExpandMore";
import ExpandLessIcon    from "@mui/icons-material/ExpandLess";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon   from "@mui/icons-material/TableChart";
import SearchIcon       from "@mui/icons-material/Search";
import RefreshIcon      from "@mui/icons-material/Refresh";

import { stockService }       from "../service/CreateStockService";
import api                    from "../service/api";
import { exportStockToExcel } from "../component/Stockexcelexport";
import { exportStockToPdf }   from "../component/Stockpdfexport";

/* ─── helpers ──────────────────────────────────────────────────────────────── */
const fmt = (v) =>
  v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const EMPTY_FILTERS = {
  model: "", skuCode: "", colour: "", location: "",
  source: "", status: "", fromDate: "", toDate: "",
  month: "", year: "", engine: "", chasis: "", search: "",
};

const STATUS_COLOR = {
  IN_STOCK: "success",
  SOLD:     "error",
  RETURN:   "warning",
};

/* ─── component ────────────────────────────────────────────────────────────── */
const AvailableStockView = () => {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewLoad, setViewLoad] = useState(false);

  const [filters, setFilters] = useState(EMPTY_FILTERS);

  // tracks which locations are expanded — none open by default
  const [expandedLocs, setExpandedLocs] = useState({});

  const toggleLoc = (loc) =>
    setExpandedLocs((prev) => ({ ...prev, [loc]: !prev[loc] }));

  const handleChange = (e) =>
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  /* ── active filter label for export header ── */
  const activeFilterLabel = () => {
    if (filters.fromDate && filters.toDate)
      return `Date Range: ${filters.fromDate} to ${filters.toDate}`;
    if (filters.month && filters.year)
      return `Month: ${new Date(0, filters.month - 1).toLocaleString("default", { month: "long" })} ${filters.year}`;
    if (filters.year)
      return `Year: ${filters.year}`;
    return "All Stock";
  };

  /* ── fetch ── */
  // CHANGED: accepts optional filters so Reset can fetch with blank filters immediately
  const fetchStock = useCallback(async (overrideFilters) => {
    const activeFilters = overrideFilters ?? filters;
    try {
      setLoading(true);
      // CHANGED: limit removed — backend now returns all matching bikes
      const res   = await stockService.filterStock(activeFilters);
      const bikes = res?.data || [];

      // group by location
      const grouped = {};
      bikes.forEach((bike) => {
        const loc = bike.location || "Unknown";
        if (!grouped[loc]) grouped[loc] = { location: loc, bikes: [], total: 0 };
        grouped[loc].bikes.push(bike);
        grouped[loc].total++;
      });

      // sort locations alphabetically
      setData(Object.values(grouped).sort((a, b) => a.location.localeCompare(b.location)));
      setError("");
    } catch (err) {
      console.error("ERROR:", err);
      setError("Failed to load stock");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchStock(); }, []); // initial load — no filters

  /* ── reset ── */
  // CHANGED: pass EMPTY_FILTERS directly instead of relying on setTimeout + stale state
  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    fetchStock(EMPTY_FILTERS);
  };

  /* ── view ── */
  const handleView = async (id) => {
    try {
      setOpenView(true);
      setViewData(null);
      setViewLoad(true);
      const res = await api.get(`/specific-bike-details/${id}`);
      setViewData(res?.data?.data || null);
    } catch {
      setError("Failed to load details");
    } finally {
      setViewLoad(false);
    }
  };

  /* ── export helpers — pass label so excel/pdf can show it ── */
  const handleExcelExport = () => exportStockToExcel(data, filters, activeFilterLabel());
  const handlePdfExport   = () => exportStockToPdf(data, filters, activeFilterLabel());

  const totalBikes = data.reduce((s, loc) => s + loc.total, 0);

  /* ── row background by status ── */
  const rowBg = (status) => {
    if (status === "SOLD")   return "#fff5f5";
    if (status === "RETURN") return "#fffbeb";
    return "inherit";
  };

  return (
    <Box sx={{ maxWidth: "1300px", mx: "auto", px: 2, pb: 4 }}>

      {/* ── HEADER ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1} pt={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Stock Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            {activeFilterLabel()}
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<TableChartIcon />}
            onClick={handleExcelExport}
            disabled={data.length === 0}
            sx={{ bgcolor: "#1d6f42", "&:hover": { bgcolor: "#155a34" }, fontWeight: 700, textTransform: "none" }}
          >
            Export Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handlePdfExport}
            disabled={data.length === 0}
            sx={{ bgcolor: "#c62828", "&:hover": { bgcolor: "#a31515" }, fontWeight: 700, textTransform: "none" }}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* ── FILTERS ── */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }} elevation={2}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5} color="text.secondary">
          FILTERS
        </Typography>
        <Grid container spacing={1.5}>

          <Grid item xs={6} sm={4} md={2}>
            <TextField label="Model" name="model" fullWidth size="small"
              value={filters.model} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField label="SKU Code" name="skuCode" fullWidth size="small"
              value={filters.skuCode} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField label="Colour" name="colour" fullWidth size="small"
              value={filters.colour} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField select label="Location" name="location" fullWidth size="small"
              value={filters.location} onChange={handleChange}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Khejuri">Khejuri</MenuItem>
              <MenuItem value="Chaitanyapur">Chaitanyapur</MenuItem>
              <MenuItem value="Nandigram">Nandigram</MenuItem>
              <MenuItem value="Ms Enterprise">Ms Enterprise</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField select label="Source" name="source" fullWidth size="small"
              value={filters.source} onChange={handleChange}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="NEW">NEW</MenuItem>
              <MenuItem value="EXCHANGE">EXCHANGE</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField select label="Status" name="status" fullWidth size="small"
              value={filters.status} onChange={handleChange}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="IN_STOCK">IN STOCK</MenuItem>
              <MenuItem value="SOLD">SOLD</MenuItem>
              <MenuItem value="RETURN">RETURN</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField label="Engine No" name="engine" fullWidth size="small"
              value={filters.engine} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField label="Chasis No" name="chasis" fullWidth size="small"
              value={filters.chasis} onChange={handleChange} />
          </Grid>

          {/* ── DATE RANGE ── */}
          <Grid item xs={6} sm={4} md={2}>
            <TextField type="date" label="From Date" name="fromDate" fullWidth size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.fromDate} onChange={handleChange} />
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField type="date" label="To Date" name="toDate" fullWidth size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.toDate} onChange={handleChange} />
          </Grid>

          {/* ── MONTH / YEAR ── */}
          <Grid item xs={6} sm={4} md={2}>
            <TextField select label="Month" name="month" fullWidth size="small"
              value={filters.month} onChange={handleChange}>
              <MenuItem value="">All</MenuItem>
              {[...Array(12)].map((_, i) => (
                <MenuItem key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString("default", { month: "long" })}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <TextField type="number" label="Year" name="year" fullWidth size="small"
              inputProps={{ min: 2000, max: 2099 }}
              value={filters.year} onChange={handleChange} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Search (model / engine / chasis / SKU)" name="search" fullWidth size="small"
              value={filters.search} onChange={handleChange} />
          </Grid>

          {/* ── ACTIONS ── */}
          <Grid item xs={6} sm={3} md={2}>
            {/* CHANGED: wrapped in arrow fn so the click event isn't passed as overrideFilters */}
            <Button fullWidth variant="contained" startIcon={<SearchIcon />}
              onClick={() => fetchStock()} sx={{ textTransform: "none" }}>
              Apply
            </Button>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Button fullWidth variant="outlined" startIcon={<RefreshIcon />}
              onClick={resetFilters} sx={{ textTransform: "none" }}>
              Reset
            </Button>
          </Grid>

        </Grid>
      </Paper>

      {/* ── STATUS ── */}
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── SUMMARY CHIPS ── */}
      {!loading && data.length > 0 && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          <Chip label={`Total: ${totalBikes} bike(s)`} color="primary" variant="filled" />
          {data.map((loc) => (
            <Chip
              key={loc.location}
              label={`${loc.location}: ${loc.total}`}
              variant="outlined"
              size="small"
            />
          ))}
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Alert severity="info">No bikes found for the selected filters.</Alert>
      )}

      {/* ── LOCATION-WISE TABLES ── */}
      {!loading && data.map((loc, i) => {
        const isOpen = !!expandedLocs[loc.location];
        return (
          <Paper key={i} sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }} elevation={2}>

            {/* ── clickable location header bar ── */}
            <Box
              onClick={() => toggleLoc(loc.location)}
              sx={{
                px: 2, py: 1.5,
                background: "linear-gradient(90deg,#1565c0,#1976d2)",
                color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                cursor: "pointer",
                userSelect: "none",
                "&:hover": { background: "linear-gradient(90deg,#0d47a1,#1565c0)" },
                transition: "background 0.2s"
              }}
            >
              <Typography fontWeight={700} fontSize={15}>
                📍 {loc.location}
              </Typography>

              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={`${loc.total} bike(s)`}
                  size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700 }}
                />
                <IconButton size="small" sx={{ color: "#fff", p: 0.25 }}>
                  {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>

            {/* ── collapsible table ── */}
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Purchase Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Model</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Engine No</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Chasis No</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Colour</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Purchase Price</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Ex-Showroom</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loc.bikes.map((b, idx) => (
                      <TableRow
                        key={b._id}
                        sx={{
                          bgcolor: rowBg(b.status),
                          "&:hover": { bgcolor: "#e3f2fd" },
                          transition: "background 0.15s"
                        }}
                      >
                        <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{idx + 1}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontSize: 13 }}>{fmt(b.date)}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{b.skuCode || "—"}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>{b.model}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{b.engine || "—"}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{b.chasis || "—"}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{b.colour || "—"}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#2e7d32", fontSize: 13 }}>
                          ₹{b.purchasePrice?.toLocaleString("en-IN") || 0}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                          ₹{b.exShowroomPrice?.toLocaleString("en-IN") || 0}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={b.status}
                            color={STATUS_COLOR[b.status] || "default"}
                            size="small"
                            sx={{ fontSize: 11, fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined"
                            onClick={() => handleView(b._id)}
                            sx={{ textTransform: "none", fontSize: 12, py: 0.25 }}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>

          </Paper>
        );
      })}

      {/* ── DETAIL MODAL ── */}
      <Dialog open={openView} onClose={() => { setOpenView(false); setViewData(null); }}
        fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>

        <Box p={3} sx={{ background: "#f8fafc" }}>
          <Typography variant="h6" fontWeight={700} mb={0.5}>Bike Full Details</Typography>
          <Divider sx={{ mb: 2 }} />

          {viewLoad && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {!viewLoad && !viewData && (
            <Alert severity="info">No data found</Alert>
          )}

          {!viewLoad && viewData && (
            <Grid container spacing={2}>

              {/* ── BIKE INFO ── */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={1.5}>🏍️ Bike Info</Typography>
                  {[
                    ["Challan No",    viewData?.stockDetails?.challanNo],
                    ["Model",         viewData?.stockDetails?.model],
                    ["SKU Code",      viewData?.stockDetails?.skuCode],
                    ["Engine No",     viewData?.stockDetails?.engine],
                    ["Chasis No",     viewData?.stockDetails?.chasis],
                    ["Colour",        viewData?.stockDetails?.colour],
                    ["Key Number",    viewData?.stockDetails?.keyNumber],
                    ["Location",      viewData?.stockDetails?.location],
                    ["Status",        viewData?.currentStatus],
                    ["Source",        viewData?.stockDetails?.source],
                    ["Purchase Date", fmt(viewData?.stockDetails?.date)],
                  ].map(([label, val]) => (
                    <Box key={label} display="flex" justifyContent="space-between"
                      py={0.4} borderBottom="1px solid #f1f5f9">
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={600}>{val || "—"}</Typography>
                    </Box>
                  ))}
                  <Box display="flex" justifyContent="space-between" pt={1}>
                    <Typography variant="body2" color="text.secondary">Purchase Price</Typography>
                    <Typography variant="body2" fontWeight={700} color="success.main">
                      ₹{viewData?.stockDetails?.purchasePrice?.toLocaleString("en-IN") || 0}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Ex-Showroom Price</Typography>
                    <Typography variant="body2" fontWeight={700}>
                      ₹{viewData?.stockDetails?.exShowroomPrice?.toLocaleString("en-IN") || 0}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* ── CREATED INFO ── */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={1.5}>👤 Created By</Typography>
                  {[
                    ["Name",  viewData?.stockDetails?.createdBy?.name],
                    ["Email", viewData?.stockDetails?.createdBy?.email],
                    ["Date",  fmt(viewData?.stockDetails?.date)],
                  ].map(([label, val]) => (
                    <Box key={label} display="flex" justifyContent="space-between"
                      py={0.4} borderBottom="1px solid #f1f5f9">
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={600}>{val || "—"}</Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>

              {/* ── TRANSFER HISTORY ── */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={1.5}>🔁 Transfer History</Typography>
                  {viewData?.transferHistory?.length > 0 ? (
                    viewData.transferHistory.map((t, i) => (
                      <Box key={i} sx={{
                        p: 1.5, mb: 1, borderRadius: 1.5,
                        background: "#f8fafc", border: "1px solid #e2e8f0"
                      }}>
                        <Typography variant="body2" fontWeight={600}>
                          {t.fromLocation} ➝ {t.toLocation}
                        </Typography>
                        {t.TransferChallanNo && (
                          <Typography variant="caption" color="text.secondary">
                            Challan: {t.TransferChallanNo}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" fontSize={12}>
                          By: {t.transferredBy?.name || "—"} &nbsp;|&nbsp; {fmt(t.date)}
                        </Typography>
                        {t.TransferCost > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Transfer Cost: ₹{t.TransferCost?.toLocaleString("en-IN")}
                          </Typography>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">No transfer history</Typography>
                  )}
                </Paper>
              </Grid>

              {/* ── SALE INFO ── */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={1.5}>💰 Sale Info</Typography>
                  {viewData?.saleDetails ? (
                    <Grid container spacing={1}>
                      {[
                        ["Customer",   viewData.saleDetails.customerName],
                        ["Sale Price", `₹${viewData.saleDetails.salePrice?.toLocaleString("en-IN")}`],
                        ["Sale Type",  viewData.saleDetails.saleType],
                        ["Sale Date",  fmt(viewData.saleDetails.saleDate)],
                      ].map(([label, val]) => (
                        <Grid item xs={6} key={label}>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                          <Typography variant="body2" fontWeight={600}>{val || "—"}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Bike not sold yet</Typography>
                  )}
                </Paper>
              </Grid>

            </Grid>
          )}

          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button variant="contained" onClick={() => { setOpenView(false); setViewData(null); }}
              sx={{ textTransform: "none" }}>
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>

    </Box>
  );
};

export default AvailableStockView;